/**
 * The values lens: what is characteristic of what was found. The wall's tiles (components/data/Wall.tsx) reduced
 * to a calm grid of value chips grouped by attribute; how many systems carry a value is a small "in 3 systems", a
 * disagreement is "3 vs 1 systems". A click hands the page a trail step (the same shape pages/Data.tsx drills with), and
 * the chip is marked data-fly so lib/fly.ts can fly it into the trail. A value already on the trail stays in the
 * grid as a chosen chip (accent, not offered again), first, so a trail chip can point back at its attribute. Facets
 * follow as rows of pills with a share bar and the documents each value is in; a facet made of several raw names
 * (lenses/facets.ts) shows the systems it came from. One record's own ids (an order id, a return id, a CRM record id:
 * identifier-shaped and different in every document) and attributes with a value per record are not what
 * characterises what was found, so they are left out; enum-like values read in sentence case ("Posti", "Pickup").
 */
import { useState } from 'react';
import type { Facet, Tile } from '../../lib/api.ts';
import { count, humanize } from '../../lib/format.ts';
import { Chip, Kicker, SystemBadge, cx } from '../../design/primitives.tsx';
import { Counted, EmptyLens, LensFrame, MorePill, plural } from './common.tsx';
import type { MergedFacet } from './facets.ts';

/**
 * A step of the trail, as pages/Data.tsx encodes it in the URL: label, the value shown, the query term; av = the
 * value as each system writes it, vs/cs = several values of a facet with their counts, r = a record rather than
 * an attribute of the whole.
 */
export interface ValueStep { l: string; v: string; t: string; av?: string[]; vs?: string[]; cs?: number[]; r?: 1 }

const term = (path: string, value: string) => `${path}=${/[\s"()]/.test(value) ? `"${value.replace(/"/g, '')}"` : value}`;

/** Narrows down to a value of a tile: in every field it came from, as every system writes it (a port of Data.tsx's drill). */
export function stepOfTile(t: Tile, value: string): ValueStep {
  const formats = t.kind === 'value' ? t.variants.find((v) => v.value === value || v.formats.includes(value))?.formats ?? [value] : [value];
  const paths = [...new Set(t.sources.map((src) => src.path))];
  const terms = [...new Set(paths.flatMap((p) => formats.map((f) => term(p, f))))];
  const has = terms.length === 1 ? terms[0] : `(${terms.join(' OR ')})`;
  // An attribute of the whole keeps the records that do not have the field at all; a value per record narrows down to the records that have it
  const q = t.kind === 'value' ? `(${has} OR (${paths.map((p) => `-${p}=*`).join(' ')}))` : has;
  return { l: t.label, v: value, t: q, av: formats, ...(t.kind === 'value' ? {} : { r: 1 as const }) };
}

/** Narrows down to one or more values of a facet, in any of its fields (a port of Data.tsx's narrow). */
export function stepOfFacet(f: Facet, values: string[]): ValueStep {
  const terms = [...new Set(f.paths.flatMap((p) => values.map((v) => term(p.path, v))))];
  return { l: f.label, v: values.join(' | '), t: terms.length === 1 ? terms[0] : `(${terms.join(' OR ')})`, vs: values, cs: values.map((v) => f.values.find((x) => x.value === v)?.count ?? 0) };
}

/** Where a pick came from, for the page's own bookkeeping. */
export interface ValuePick { value: string; tile?: Tile; facet?: Facet }

const FIRST = 12;
const FACET_FIRST = 6;

/** A label that says the value is an identifier (Id, Order id, Customer number, Reference). */
const ID_LABEL = /\b(id|ids|uuid|guid|key|number|no|nr|num|ref|reference|identifier|code)\b|(?<=[a-z])(Id|No|Nr|Num|Ref|Key|Code)$/i;
/** A value shaped like an identifier: letters with digits (SO-763643, RMA-763583, 0015g00005C95675E2), a long hex or digit string. */
export const idShaped = (v: string) => (/^[\w./:+@-]+$/.test(v) && /[A-Za-z]/.test(v) && /\d/.test(v)) || /^[0-9a-f-]{20,}$/i.test(v) || /^\d{12,}$/.test(v);
/** Dates, ids and codes are set in mono; a name, a city, an amount or a postcode is not. */
export const isId = (value: string, label: string) => ISO.test(value) || idShaped(value) || idLabel(label);
const idLabel = (label: string) => ID_LABEL.test(label) || ID_LABEL.test(humanize(label));
const ISO = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?)?/;

/** An identifier with (nearly) as many values as a kind has records is a value per record, not a characteristic. */
const PER_RECORD = 0.75;

/**
 * An attribute that is one record's own: an identifier whose every source holds as many values as documents (a CRM
 * record id, a return id), or one with about as many values as one of its data models has records (an order number
 * over 118 orders, a shipment id over 59 deliveries). `records` is the record count by data model.
 */
export function isOwnId(t: Tile, records?: Map<string, number>): boolean {
  const unique = t.sources.length > 0 && t.sources.every((s) => s.present > 0 && s.distinct >= s.present);
  const idLike = idLabel(t.label) || (t.kind === 'value' ? idShaped(t.value) : t.values.slice(0, 3).some((v) => idShaped(v.value)));
  if (idLike && unique) return true;
  if (!records) return false;
  const counts = t.classes.map((c) => records.get(c) ?? 0);
  // One value in the documents of single-record kinds only (a CRM record id over 9 documents of the one customer)
  if (t.kind === 'value') return idLike && counts.length > 0 && counts.every((n) => n === 1);
  return counts.some((n) => n > 1 && (t.distinct >= n || (idLike && t.distinct >= n * PER_RECORD)));
}

/** "POSTI" → "Posti", "HOME_DELIVERY" → "Home delivery", "delivery" → "Delivery": an enum-like word in sentence case; codes (EUR, ERP) and ids stay. */
export function sentenceCase(v: string): string {
  if (idShaped(v)) return v;
  if (/^[a-z][a-z_ ]{2,}$/.test(v)) return v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' ');
  if (!/^[A-Z][A-Z_ ]{4,}$/.test(v)) return v;
  const words = v.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The families of an address, in reading order. */
const ADDRESS = ['street', 'city', 'postcode', 'country'];

/**
 * The order of the grid: the wall's order, with the attributes of a known family first, the address (street, city,
 * postcode) as one block in reading order right after the name, and raw-named attributes last.
 */
function order(tiles: Tile[]): Tile[] {
  const name = tiles.findIndex((t) => t.family === 'name');
  const firstAddress = tiles.findIndex((t) => !!t.family && ADDRESS.includes(t.family));
  const addressAt = Math.max(name, firstAddress) + 0.5;
  const key = (t: Tile, i: number) => {
    if (!t.family) return 1000 + i;
    const a = ADDRESS.indexOf(t.family);
    return a >= 0 ? addressAt + a * 0.01 : i;
  };
  return tiles.map((t, i) => ({ t, k: key(t, i) })).sort((a, b) => a.k - b.k).map((x) => x.t);
}

export function ValuesLens({ wall, facets, records, onPick, onValue, hidden = [], view, onClose }: {
  wall: Tile[];
  /** Records by data model: an attribute with that many values is a value per record, not a characteristic. */
  records?: Map<string, number>;
  /** One per concept (mergeFacets), in the order they came. */
  facets: MergedFacet[];
  /** A value chosen: the step for the trail, and where it came from. */
  onPick: (step: ValueStep, from: ValuePick) => void;
  /** "all 94" on an attribute with a value per record: the page shows them (a list, a peek). */
  onValue?: (tile: Tile) => void;
  /** Values already on the trail: shown as chosen, not offered again. */
  hidden?: string[];
  /** The "all attributes" and "common values" toggles, when the page keeps them in the URL. */
  view?: { all: boolean; common: boolean; onChange: (change: { all?: boolean; common?: boolean }) => void };
  onClose?: () => void;
}) {
  const [allLocal, setAllLocal] = useState(false);
  const [commonLocal, setCommonLocal] = useState(false);
  const all = view ? view.all : allLocal;
  const common = view ? view.common : commonLocal;
  const setAll = (f: (a: boolean) => boolean) => (view ? view.onChange({ all: f(all) }) : setAllLocal(f));
  const setCommon = (f: (a: boolean) => boolean) => (view ? view.onChange({ common: f(common) }) : setCommonLocal(f));
  const chosen = new Set(hidden.map((v) => v.toLowerCase()));
  const isChosen = (value: string, formats: string[] = []) => [value, ...formats].some((x) => chosen.has(x.toLowerCase()));
  const onTrail = (t: Tile) => t.kind === 'value' && t.variants.some((v) => isChosen(v.value, v.formats));
  // The searched thing itself is not an attribute of what was found; a value narrowed down to (the cloud anchors it too) is shown as chosen
  const shown = wall.filter((t) => (!t.anchor && !isOwnId(t, records)) || onTrail(t));
  // The chosen first, then the attributes with a known family (an address in reading order), then the raw-named ones
  const specific = [...order(shown.filter((t) => t.specific !== false || onTrail(t)))].sort((a, b) => Number(onTrail(b)) - Number(onTrail(a)));
  const rest = shown.filter((t) => t.specific === false && !onTrail(t));
  const first = all || specific.length <= FIRST + 3 ? specific : specific.slice(0, FIRST);
  const conflicts = specific.filter((t) => t.state === 'conflict').length;
  const sentence = (
    <>
      {count(specific.length)} {plural(specific.length, 'attribute')}
      {conflicts > 0 && <> · <span className="text-warn">{conflicts} {plural(conflicts, 'disagreement')}</span></>}
      {rest.length > 0 && <> · {count(rest.length)} common</>}
    </>
  );
  const pick = (t: Tile, v: string) => onPick(stepOfTile(t, v), { value: v, tile: t });

  return (
    <LensFrame title="characteristic values" count={sentence} onClose={onClose}>
      {specific.length === 0 && rest.length === 0 && <EmptyLens>Nothing sets what was found apart yet.</EmptyLens>}
      {first.length > 0 && <Grid tiles={first} onPick={pick} onValue={onValue} chosen={isChosen} />}
      {(specific.length > FIRST + 3 || rest.length > 0) && (
        <div className="flex justify-center gap-2 flex-wrap">
          {specific.length > FIRST + 3 && <MorePill onClick={() => setAll((a) => !a)}>{all ? 'fewer' : `all ${count(specific.length)} attributes`}</MorePill>}
          {rest.length > 0 && <MorePill onClick={() => setCommon((c) => !c)}>{common ? 'hide common values' : `${count(rest.length)} common ${plural(rest.length, 'value')}`}</MorePill>}
        </div>
      )}
      {common && rest.length > 0 && (
        <div className="flex flex-col gap-3 mv-rise">
          <p className="m-0 text-small text-n7">Found here, but in most of the data anyway.</p>
          <Grid tiles={rest} onPick={pick} onValue={onValue} dim />
        </div>
      )}
      {facets.length > 0 && (
        <section className="flex flex-col gap-3 pt-2">
          <Kicker>narrow down by</Kicker>
          <div className="flex flex-col gap-2.5">
            {facets.map((f) => <FacetRow key={f.id} f={f} onPick={(v) => onPick(stepOfFacet(f, [v]), { value: v, facet: f })} />)}
          </div>
        </section>
      )}
    </LensFrame>
  );
}

function Grid({ tiles, onPick, onValue, dim, chosen }: { tiles: Tile[]; onPick: (t: Tile, v: string) => void; onValue?: (t: Tile) => void; dim?: boolean; chosen?: (value: string, formats?: string[]) => boolean }) {
  return (
    <div className={cx('grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-x-6 gap-y-4', dim && 'opacity-70')}>
      {tiles.map((t, i) => <Attribute key={t.id} t={t} onPick={(v) => onPick(t, v)} onValue={onValue && (() => onValue(t))} delay={i} chosen={chosen} />)}
    </div>
  );
}

/** "3 vs 1": how many systems have each value. */
const versus = (t: Tile) => t.variants.map((v) => v.systems.length).join(' vs ');

/**
 * One attribute: its label with how deep the value goes ("in 3 systems", or "3 vs 1 systems" when they disagree),
 * then its value(s) as chips; a disagreement shows every variant, the odd ones out with a warn dot. A chosen value
 * (on the trail) is an accent chip that does nothing.
 */
function Attribute({ t, onPick, onValue, delay, chosen }: { t: Tile; onPick: (v: string) => void; onValue?: () => void; delay: number; chosen?: (value: string, formats?: string[]) => boolean }) {
  const label = humanize(t.label);
  const conflict = t.state === 'conflict';
  const failed = t.sources.some((s) => s.failure);
  const systems = t.systems.length;
  return (
    <div data-tile={t.id} className="flex flex-col gap-1.5 min-w-0 mv-rise scroll-mt-20" style={{ animationDelay: `${Math.min(delay, 24) * 20}ms` }}>
      <div className="flex items-baseline gap-2 text-small text-n7 min-w-0">
        <span className="truncate">{label}</span>
        {conflict
          ? <span className="text-micro text-warn whitespace-nowrap">{versus(t)} systems</span>
          : systems > 1 && <span className="text-micro whitespace-nowrap">in {systems} systems</span>}
        {failed && <span className="text-micro text-fail whitespace-nowrap">update failed</span>}
        {t.kind === 'many' && <span className="text-micro whitespace-nowrap"><Counted n={t.distinct} word="different values" /></span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {t.kind === 'value'
          ? (conflict ? t.variants : t.variants.slice(0, 1)).map((v, i) => {
            const on = !!chosen?.(v.value, v.formats);
            return <ValueChip key={v.value} value={v.value} label={t.label} tone={on ? 'accent' : conflict && i > 0 ? 'warn' : 'neutral'} onClick={on ? undefined : () => onPick(v.value)} />;
          })
          : (
            <>
              {t.values.slice(0, 3).map((v) => <ValueChip key={v.value} value={v.value} label={t.label} onClick={() => onPick(v.value)} />)}
              {onValue && t.distinct > 3 && (
                <button type="button" onClick={onValue} className="h-8 px-3 rounded-chip bg-transparent text-small font-medium text-accent border-0 cursor-pointer hover:bg-accent-soft transition-colors duration-[var(--t-micro)] ease-mv">
                  all <Counted n={t.distinct} word="values" />
                </button>
              )}
            </>
          )}
      </div>
    </div>
  );
}

function ValueChip({ value, label, tone = 'neutral', onClick }: { value: string; label: string; tone?: 'neutral' | 'warn' | 'accent'; onClick?: () => void }) {
  const id = isId(value, label);
  return (
    <Chip tone={tone} onClick={onClick} data-fly={onClick ? 'value' : undefined} className="max-w-full">
      <span className={cx(id && 'font-mono')}>{id ? value : sentenceCase(value)}</span>
    </Chip>
  );
}

/**
 * An attribute whose values split what was found: pills with the documents each value is in, as a share bar. A facet
 * merged from several raw names carries the systems it came from.
 */
function FacetRow({ f, onPick }: { f: MergedFacet; onPick: (value: string) => void }) {
  const [all, setAll] = useState(false);
  const shown = all ? f.values : f.values.slice(0, FACET_FIRST);
  const top = f.values[0]?.count || 1;
  return (
    <div className="grid grid-cols-[minmax(0,180px)_minmax(0,1fr)] gap-x-4 gap-y-1 items-baseline">
      <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-small text-n7 min-w-0">
        <span>{humanize(f.label)}</span>
        <span className="text-micro whitespace-nowrap">· <span className="font-mono tabular-nums">{count(f.distinct)}{f.more ? '+' : ''}</span> {plural(f.distinct, 'value')}</span>
        {f.sources.length > 1 && f.systems.map((s) => <SystemBadge key={s} code={s} size={20} />)}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((v) => (
          <button key={v.value} type="button" onClick={() => onPick(v.value)} data-fly="value"
            className="relative overflow-hidden inline-flex items-center gap-2 h-7 pl-3 pr-2.5 rounded-full bg-n3 text-small text-n9 border-0 cursor-pointer max-w-[320px] transition-colors duration-[var(--t-micro)] ease-mv hover:bg-n4">
            <span className="absolute inset-y-0 left-0 bg-accent-soft" style={{ width: `${(v.count / top) * 100}%` }} aria-hidden="true" />
            <span className={cx('relative truncate', isId(v.value, f.label) && 'font-mono')}>{isId(v.value, f.label) ? v.value : sentenceCase(v.value)}</span>
            <span className="relative text-micro text-n7"><Counted n={v.count} word={plural(v.count, 'document')} /></span>
          </button>
        ))}
        {f.values.length > FACET_FIRST && (
          <button type="button" onClick={() => setAll((a) => !a)} className="h-7 px-3 rounded-full bg-transparent text-small text-accent border-0 cursor-pointer hover:bg-accent-soft transition-colors duration-[var(--t-micro)] ease-mv">
            {all ? 'fewer' : `+ ${f.values.length - FACET_FIRST} more`}
          </button>
        )}
      </div>
    </div>
  );
}
